import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';

import MobileAppBar from '@/components/molecules/MobileAppBar';
import InfoModal from '@/components/molecules/InfoModal';
import BudgetCreationSteps from '@/components/molecules/budget/BudgetCreationSteps';
import BudgetCreationStepsMobile from '@/components/molecules/budget/BudgetCreationStepsMobile';
import BudgetMemberCalculationsFooter from '@/components/molecules/budget/BudgetMemberCalculationsFooter';
import BudgetCreationForm, { BudgetCreationFormHandle } from '@/components/organisms/BudgetCreationForm';
import { BUDGET_EDIT_HEADER } from '@/components/organisms/budget/budgetCreation.constants';
import { Button } from '@/components/ui/button';
import gearIcon from '@/assets/icons/gear.svg';
import profileDiscardIcon from '@/assets/icons/profile-discard.svg';
import { budgetService } from '@/services/budget.service';
import { hslToHex, hexToHsl } from '@/utils/ColorConverter';
import { useNavigationGuard } from '@/hooks/useNavigationGuard';
import { BudgetMemberCalculations } from '@/utils/budgetCalculations';

const EditPage = () => {
  const navigate = useNavigate();
  const { budgetId } = useParams<{ budgetId: string }>();
  const formRef = useRef<BudgetCreationFormHandle>(null);
  const [activeStep, setActiveStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [budgetData, setBudgetData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [memberCalculations, setMemberCalculations] = useState<BudgetMemberCalculations | null>(null);

  const { isDiscardModalOpen, handleCancel, handleDiscardChanges, handleDiscardModalClose, allowNavigation } =
    useNavigationGuard({
      isDirty: isFormDirty,
      fallbackPath: `/budgets/${budgetId}`
    });

  useEffect(() => {
    const fetchBudget = async () => {
      if (!budgetId) {
        setError('Budget ID is missing');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const response = await budgetService.getBudget(budgetId);
        const budget = response.data;

        const formattedBudget = {
          budgetName: budget.name,
          budgetType: budget.type,
          budgetIcon: budget.icon_id,
          budgetColor: hexToHsl(budget.color_hex),
          description: budget.description || '',
          startDate: new Date(budget.start_date),
          endDate: new Date(budget.end_date),
          period: budget.period || 'one_month',
          currency: budget.currency,
          autoRenewal: budget.is_auto_renewal,
          bmev: (budget.bmev + (budget?.bmev_used || 0))?.toString() || '',
          bmevThreshold: budget.bmev_alert_threshold,
          btrt: budget.btrt?.toString() || '',
          btrtThreshold: budget.btrt_alert_threshold
        };

        setBudgetData(formattedBudget);
      } catch (error) {
        console.error('Error fetching budget:', error);
        setError('Failed to load budget. Please try again.');
        toast.error('Failed to load budget data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchBudget();
  }, [budgetId]);

  const handleFormSubmit = async (data: any) => {
    if (!budgetId) return;

    try {
      setIsSubmitting(true);

      const colorHex = hslToHex(data.budgetColor);

      const updatedBudgetData = {
        name: data.budgetName,
        type: data.budgetType,
        icon_id: data.budgetIcon,
        color_hex: colorHex,
        description: data.description || '',
        start_date: data.startDate.toISOString().split('T')[0],
        end_date: data.endDate.toISOString().split('T')[0],
        period: data.period,
        currency: data.currency,
        is_auto_renewal: data.autoRenewal,
        bmev: data.bmev ? Number(data.bmev) : 0,
        bmev_alert_threshold: data.bmevThreshold,
        btrt: data.btrt ? Number(data.btrt) : 0,
        btrt_alert_threshold: data.btrtThreshold
      };

      await budgetService.updateBudget(budgetId, updatedBudgetData);

      toast.success('Budget updated successfully!');
      allowNavigation();
      navigate('/budgets/' + budgetId);
    } catch (error) {
      console.error('Error updating budget:', error);
      toast.error('Failed to update budget. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLeftButtonClick = () => {
    if (activeStep === 1) {
      handleCancel();
      return;
    }
    setActiveStep(1);
  };

  const handleRightButtonClick = async () => {
    if (activeStep === 1) {
      const isValid = await formRef.current?.validateStep(1);
      if (isValid) {
        setActiveStep(2);
      }
      return;
    }

    const isValid = await formRef.current?.validateStep(2);
    if (isValid) {
      formRef.current?.submit();
    }
  };

  const isLastStep = activeStep === 2;

  if (isLoading) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>;
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <Button onClick={() => navigate('/budgets')}>Return to Budgets</Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <MobileAppBar title="Edit Budget" onRightIconClick={() => {}} rightIcon={gearIcon} />

      <div className="bg-[#FBFBFF] p-4 md:bg-white">
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-center font-bold mb-5 md:text-left md:text-[24px] md:mb-6">
            {BUDGET_EDIT_HEADER[activeStep]}
          </h2>

          <BudgetCreationSteps activeStep={activeStep} />
        </div>

        <div className="md:hidden mb-4">
          <BudgetCreationStepsMobile activeStep={activeStep} />
        </div>

        {budgetData && (
          <BudgetCreationForm
            ref={formRef}
            activeStep={activeStep}
            onSubmit={handleFormSubmit}
            initialValues={budgetData}
            onFormStateChange={({ isDirty }) => setIsFormDirty(isDirty)}
            onMemberCalculationsChange={setMemberCalculations}
          />
        )}
      </div>

      {isDiscardModalOpen && (
        <InfoModal
          isOpen={isDiscardModalOpen}
          variant="confirm"
          title="Are you sure you want to discard changes?"
          message="Unsaved changes will not be saved into your budget"
          buttonText="Discard"
          onButtonClick={handleDiscardChanges}
          icon={profileDiscardIcon}
          onClose={handleDiscardModalClose}
        />
      )}

      <footer className="flex items-end justify-between gap-4 px-4 py-7 bg-gradient-to-b from-[#F0EFFD]/0 from-0% to-[#F0EFFD] to-[17.74%] md:bg-white md:bg-none md:from-transparent md:to-transparent">
        <div className="flex-1 min-w-0">
          {activeStep === 2 && <BudgetMemberCalculationsFooter calculations={memberCalculations} />}
        </div>
        <div className="md:flex md:justify-end md:gap-4 shrink-0">
          <Button
            className="hidden md:block rounded-[24px] text-base font-semibold px-6 h-[44px] text-[16px] w-full md:w-fit text-[#E8507E]"
            variant={'text'}
            onClick={handleLeftButtonClick}
            disabled={isSubmitting}
            type="button"
          >
            {activeStep === 1 ? 'CANCEL' : 'PREVIOUS'}
          </Button>
          <Button
            className="rounded-[24px] text-base font-semibold px-6 h-[44px] text-[16px] w-full md:w-fit"
            onClick={handleRightButtonClick}
            disabled={isSubmitting}
            type="button"
          >
            {isSubmitting ? 'SAVING...' : isLastStep ? 'SAVE CHANGES' : 'NEXT'}
          </Button>
        </div>
      </footer>
    </>
  );
};

export default EditPage;
